import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useCompanyProfile, useUpdateCompany } from '@/features/settings/settings.api';
import { BrandLogo } from '@/shared/brand/BrandLogo';
import { isOnboarded, markOnboarded } from './onboarding';

// Zonas horarias frecuentes para clientes en la región (Perú primero).
const TIMEZONES = [
  { value: 'America/Lima', label: 'Lima (GMT-5)' },
  { value: 'America/Bogota', label: 'Bogotá (GMT-5)' },
  { value: 'America/Guayaquil', label: 'Guayaquil (GMT-5)' },
  { value: 'America/La_Paz', label: 'La Paz (GMT-4)' },
  { value: 'America/Santiago', label: 'Santiago (GMT-4/-3)' },
  { value: 'America/Caracas', label: 'Caracas (GMT-4)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (GMT-3)' },
  { value: 'America/Mexico_City', label: 'Ciudad de México (GMT-6)' },
];

export function WelcomePage(): JSX.Element {
  const navigate = useNavigate();
  const tenantId = useAuthStore((s) => s.activeTenantId);
  const user = useAuthStore((s) => s.user);
  const profile = useCompanyProfile();
  const update = useUpdateCompany();
  const [tz, setTz] = useState('America/Lima');

  // Precarga la zona del tenant cuando llega el perfil.
  useEffect(() => {
    if (profile.data?.timezone) setTz(profile.data.timezone);
  }, [profile.data?.timezone]);

  // Si ya completó la bienvenida, no la repetimos.
  if (isOnboarded(tenantId)) {
    return <Navigate to="/" replace />;
  }

  const finish = (): void => {
    update.mutate(
      { timezone: tz },
      {
        onSuccess: () => {
          markOnboarded(tenantId);
          navigate('/', { replace: true, state: { startTour: true } });
        },
      },
    );
  };

  const firstName = (user?.name ?? '').split(' ')[0];

  return (
    <div
      className="cx"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background:
          'radial-gradient(900px 500px at 12% 0%, rgba(120,210,240,.20), transparent 60%), radial-gradient(800px 480px at 100% 100%, rgba(171,239,255,.18), transparent 60%), #EEF1F6',
      }}
    >
      <div className="card fade-in" style={{ width: 480, maxWidth: '100%', padding: '36px 40px', boxShadow: 'var(--sh-lg)', borderRadius: 24 }}>
        <BrandLogo variant="wordmark-dark" height={44} style={{ marginBottom: 22 }} />
        <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.02em', margin: '0 0 6px' }}>
          {firstName ? `¡Hola, ${firstName}! 👋` : '¡Bienvenido a Cajita! 👋'}
        </h1>
        <p className="muted t-sm" style={{ margin: '0 0 26px' }}>
          Antes de empezar, confirma la zona horaria de tu negocio. La usamos para fechar tus
          comprobantes, cierres de caja y reportes.
        </p>

        <div className="field">
          <label className="label">Zona horaria</label>
          <div className="input-icon">
            <Clock size={17} />
            <select className="input" value={tz} onChange={(e) => setTz(e.target.value)} style={{ appearance: 'auto' }}>
              {TIMEZONES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          className="btn btn-primary btn-lg btn-block"
          type="button"
          onClick={finish}
          disabled={update.isPending}
          style={{ marginTop: 24 }}
        >
          {update.isPending ? 'Guardando…' : 'Comenzar el recorrido'}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
